import { yupResolver } from '@hookform/resolvers/yup';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { useTheme } from '@mui/material/styles';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import {
  Order_Update,
  patchV1CompaniesCompanyIdJobsId,
  patchV1CompaniesCompanyIdOrdersId,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import TreadLiveMapV2 from '~components/Maps/v2/TreadLiveMapV2';
import UnreadCountBadge from '~components/Messaging/UnreadCountBadge';
import { unreadTabAccentSx } from '~components/Messaging/unreadTabAccentSx';
import OrderAuditLog from '~components/Order/AuditLog/OrderAuditLog';
import LoadsTabContent from '~components/Order/LoadsTab/LoadsTabContent';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import TabLabelWithAdornment from '~components/Tabs/TabLabelWithAdornment';
import { FeatureFlags } from '~constants/featureFlags';
import { NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX } from '~constants/filters';
import { Job } from '~hooks/useJob/models';
import { Order } from '~hooks/useOrders/models';
import MessagingProviders from '~pages/Messages/MessagingProviders';
import useMessagingOrderUnreadCountQuery from '~queries/useMessagingOrderUnreadCountQuery';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { LoadsJobFilters } from '~types/loadsFilters';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';
import { userCompanyOwnsOrder } from '~utils/orders';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { doneOrderStates } from '../constants/orderStates';
import { OrderDetails } from '../OrderDetails';
import { OrdersPresentationMode } from './constants';
import {
  EditJobTableRowsDto,
  makeEditJobTableRowsSchema,
} from './editJobTableRowsSchema';
import {
  buildJobUpdateBodyFromRow,
  DirtySingleJobTableRow,
  makeEditJobTableRowDto,
} from './editJobTableRowsUtils';
import JobsTable from './JobsTable';
import OrderMessagingTab from './OrderMessaging/OrderMessagingTab';
import OrderMetricsTab from './OrderMetricsTab';
import OrderTickets from './OrderTickets';
import OrderTrucksCountForm, {
  OrderTrucksCountFormHandler,
} from './OrderTrucksCountForm';
import trackOrderDispatchTabViewed, {
  OrderDispatchCardExpandedBlockTab,
} from './trackOrderDispatchTabViewed';
import TruckReplayTab from './TruckReplay/TruckReplayTab';

interface OrderDispatchCardExpandedBlockProps {
  checkedJobs?: string[];
  isMessagingTabRequested?: boolean;
  onCheckedJobStateChange?: (jobId: string) => void;
  onCreateJobFromOrder: () => void;
  onMessagingTabRequestHandled?: () => void;
  order: Order;
  presentation?: OrdersPresentationMode;
  readOnly: boolean;
}

enum EditMode {
  JOBS_TABLE_ROWS = 'job-table-rows',
  ORDER_TRUCKS_COUNT = 'order-trucks-count',
}

const OrderDispatchCardExpandedBlock = observer(
  ({
    checkedJobs,
    isMessagingTabRequested,
    onCheckedJobStateChange,
    onCreateJobFromOrder,
    onMessagingTabRequestHandled,
    order,
    presentation = 'cards',
    readOnly,
  }: OrderDispatchCardExpandedBlockProps) => {
    const theme = useTheme();
    const { userStore, ordersDispatchStore, toasterStore } = useStores();
    const effectiveCompanyId = getEffectiveUserCompanyId(userStore);
    const [chosenTab, setChosenTab] = useState(
      isMessagingTabRequested
        ? OrderDispatchCardExpandedBlockTab.MESSAGING
        : OrderDispatchCardExpandedBlockTab.JOBS,
    );

    const [messagingTabRequestCount, setMessagingTabRequestCount] = useState(0);
    const [jobsTableActionsContainer, setJobsTableActionsContainer] =
      useState<HTMLElement | null>(null);
    const [jobsTableColumnsMenuContainer, setJobsTableColumnsMenuContainer] =
      useState<HTMLElement | null>(null);
    const isMountRequestPending = useRef(Boolean(isMessagingTabRequested));

    useEffect(() => {
      if (!isMessagingTabRequested) return;

      setChosenTab(OrderDispatchCardExpandedBlockTab.MESSAGING);
      if (isMountRequestPending.current) {
        isMountRequestPending.current = false;
      } else {
        setMessagingTabRequestCount((count) => count + 1);
      }
      onMessagingTabRequestHandled?.();
    }, [isMessagingTabRequested, onMessagingTabRequestHandled]);

    const [editMode, setEditMode] = useState<EditMode>();
    const [isUpdatingAfterEditMode, setIsUpdatingAfterEditMode] = useState(false);
    const jobsTablePresentation: OrdersPresentationMode =
      editMode === EditMode.JOBS_TABLE_ROWS ? 'cards' : presentation;

    const { canCreateJob, canEditJob } = userStore.getPermissions();
    const canAddNewJobs =
      order.editable &&
      !doneOrderStates.includes(order.state) &&
      canCreateJob &&
      !readOnly;
    const canEditJobs = order.editable && canEditJob && !readOnly;

    const vendorJobSplittingEnabled = useFeatureFlag({
      featureFlagKey: FeatureFlags.vendorJobSplittingEnabled,
    });
    const { unreadCount: orderUnreadCount } = useMessagingOrderUnreadCountQuery(order);

    // Extract filters from store to pass to LoadsTabContent
    const loadsFilters = useMemo((): LoadsJobFilters => {
      const storeFilters = ordersDispatchStore.filters;
      return {
        driverIds: storeFilters.driverIds,
        dropoffSiteIds: storeFilters.dropoffSites,
        materialIds: storeFilters.materialIds,
        pickupSiteIds: storeFilters.pickUpSites,
        states: storeFilters.jobStates as LoadsJobFilters['states'],
        ticketPotentialDuplicate: storeFilters.ticketPotentialDuplicate?.length
          ? storeFilters.ticketPotentialDuplicate[0] === 'true'
          : undefined,
        vendorAccountIds: storeFilters.vendorAccountIds,
      };
    }, [ordersDispatchStore.filters]);

    const editJobTableRowsSchema = useMemo(
      () => makeEditJobTableRowsSchema(vendorJobSplittingEnabled),
      [vendorJobSplittingEnabled],
    );
    const editJobTableRowsForm = useForm<EditJobTableRowsDto>({
      resolver: yupResolver(editJobTableRowsSchema),
    });
    const orderTrucksCountFormRef = useRef<OrderTrucksCountFormHandler>(null);

    const handleCancelEditModeClick = useCallback(() => {
      editJobTableRowsForm.reset();
      setEditMode(undefined);
    }, []);

    const handleJobsTableRowsFormSubmit = useCallback(() => {
      editJobTableRowsForm.handleSubmit(async (formData) => {
        setIsUpdatingAfterEditMode(true);

        const orderJobs = ordersDispatchStore.orderJobs.get(order.id) ?? [];
        // Mirrors EditJobRatesDialog: only the order-owning company may write
        // (or clear) the job's site-rate-schedule stamp.
        const canClearSiteRateScheduleStamp = userCompanyOwnsOrder(userStore, order);

        type JobUpdateResult = { jobId: string; updatedJob: Job | null };

        const jobUpdateRequests = Object.entries(formData).map(
          async ([jobId, singleJobTableRowFormData]): Promise<JobUpdateResult | null> => {
            const dirtyRow = editJobTableRowsForm.formState.dirtyFields[jobId];

            if (!dirtyRow) {
              return null;
            }

            const job = orderJobs.find((orderJob) => orderJob.id === jobId);
            const body = buildJobUpdateBodyFromRow(
              singleJobTableRowFormData,
              dirtyRow as DirtySingleJobTableRow,
              job,
              { canClearSiteRateScheduleStamp },
            );

            try {
              const response = await patchV1CompaniesCompanyIdJobsId({
                path: { 'company-id': effectiveCompanyId, id: jobId },
                body,
              });

              return { jobId, updatedJob: Job.parse(response.data.data) };
            } catch (error) {
              console.error('Failed to update job:', error);

              return { jobId, updatedJob: null };
            }
          },
        );

        const results = (await Promise.all(jobUpdateRequests)).filter(
          (result): result is JobUpdateResult => result !== null,
        );
        const succeeded = results.filter(
          (result): result is { jobId: string; updatedJob: Job } =>
            result.updatedJob instanceof Job,
        );
        const failed = results.filter((result) => !result.updatedJob);

        succeeded.forEach(({ updatedJob }) => {
          ordersDispatchStore.upsertJob(updatedJob);
        });

        if (failed.length > 0) {
          // Stay in edit mode so the failed rows keep their unsaved values;
          // re-baseline the rows that did save so a retry re-submits only the
          // failures (a re-PATCH of an unchanged rate would re-stamp pricing
          // history).
          succeeded.forEach(({ jobId, updatedJob }) => {
            editJobTableRowsForm.resetField(jobId, {
              defaultValue: makeEditJobTableRowDto(updatedJob, order),
            });
          });

          const failedJobNames = failed
            .map(
              ({ jobId }) =>
                orderJobs.find((orderJob) => orderJob.id === jobId)?.jobId ?? jobId,
            )
            .join(', ');

          toasterStore.push(
            alert(
              t('error_messages.jobs.failed_to_update_some_jobs', {
                jobs: failedJobNames,
              }),
              AlertTypes.error,
            ),
          );
        } else {
          setEditMode(undefined);
        }

        setIsUpdatingAfterEditMode(false);
      })();
    }, [
      editJobTableRowsForm.handleSubmit,
      editJobTableRowsForm.formState.dirtyFields,
      editJobTableRowsForm.resetField,
      order,
    ]);

    const handleOrderTrucksCountFormSubmit = useCallback(async () => {
      const formData = await orderTrucksCountFormRef.current?.onSubmit();

      if (!formData) {
        return;
      }

      try {
        setIsUpdatingAfterEditMode(true);

        const body: Order_Update = { truck_count: formData.trucksCount };
        const response = await patchV1CompaniesCompanyIdOrdersId({
          path: { 'company-id': order.company?.id ?? effectiveCompanyId, id: order.id },
          body,
        });
        const updatedOrder = Order.parse(response.data.data);

        ordersDispatchStore.upsertOrder(updatedOrder);
      } catch (error) {
        connection.handleRequestError(error, t('error_messages.orders.failed_to_update'));
      } finally {
        setIsUpdatingAfterEditMode(false);
        setEditMode(undefined);
      }
    }, [order.id, effectiveCompanyId]);

    const handleFormSubmit = useCallback(() => {
      if (editMode === EditMode.JOBS_TABLE_ROWS) {
        handleJobsTableRowsFormSubmit();
      } else {
        handleOrderTrucksCountFormSubmit();
      }
    }, [editMode, handleJobsTableRowsFormSubmit]);

    const userAccess = userStore.getAccess();
    const canShowTimeline = order.editable || userAccess.assignedTabForeman;

    const visibleTabs = [
      OrderDispatchCardExpandedBlockTab.JOBS,
      OrderDispatchCardExpandedBlockTab.LOADS,
      ...(order.editable ? [OrderDispatchCardExpandedBlockTab.METRICS] : []),
      OrderDispatchCardExpandedBlockTab.TICKETS,
      OrderDispatchCardExpandedBlockTab.ORDER_DETAILS,
      OrderDispatchCardExpandedBlockTab.LIVE_MAP,
      OrderDispatchCardExpandedBlockTab.TRUCK_REPLAY,
      ...(canShowTimeline ? [OrderDispatchCardExpandedBlockTab.TIMELINE] : []),
      ...(order.messagingEnabled ? [OrderDispatchCardExpandedBlockTab.MESSAGING] : []),
    ];

    // A flag can hide the chosen tab, so fall back to jobs before `indexOf` hands Tabs a -1, and
    // adopt the fallback so the hidden tab does not take the panel back when the flag returns.
    const selectedTab = visibleTabs.includes(chosenTab)
      ? chosenTab
      : OrderDispatchCardExpandedBlockTab.JOBS;
    if (selectedTab !== chosenTab) {
      setChosenTab(selectedTab);
    }

    const handleSelectedTabChange = useCallback(
      (__: React.SyntheticEvent, value: number) => {
        // Map the Material-UI tab index to the correct enum value
        const nextTab = visibleTabs[value];
        setChosenTab(nextTab);
        trackOrderDispatchTabViewed(order, nextTab, 'clicked');
      },
      [visibleTabs, order],
    );

    const hasUnreadMessages = orderUnreadCount > 0;
    const isUnreadMessagingTabSelected =
      hasUnreadMessages && selectedTab === OrderDispatchCardExpandedBlockTab.MESSAGING;
    const selectedTabColor = isUnreadMessagingTabSelected
      ? theme.brandV2.colors.treadRed
      : theme.brandV2.colors.treadBlack;

    return (
      <Box>
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
            px: 1,
          }}
        >
          <Tabs
            value={visibleTabs.indexOf(selectedTab)}
            onChange={handleSelectedTabChange}
            sx={{
              '&.MuiTabs-root': { minHeight: 'auto', px: 1, mt: 2 },
              '& .MuiTabs-flexContainer': { gap: 1 },
              '& .MuiTabs-indicator': {
                backgroundColor: selectedTabColor,
                borderRadius: 0,
                height: '2px',
              },
              '& .MuiTab-root': {
                fontSize: '12px',
                lineHeight: '32px',
                minHeight: 'auto',
                minWidth: 'auto',
                px: 1,
                py: 0,
                '&.Mui-selected': { color: selectedTabColor },
              },
            }}
          >
            <Tab
              label={t('form_fields.jobs')}
              data-ui={`page-dispatch__order-dispatch-card-expanded-block__order-${order.orderId}__interactive`}
            />
            <Tab
              label={t('common.loads')}
              data-ui={`page-dispatch__order-dispatch-card-expanded-block__order-${order.orderId}__interactive`}
            />
            {order.editable ? (
              <Tab
                label={t('dispatch.order.metrics.metrics')}
                data-ui={`page-dispatch__order-dispatch-card-expanded-block__order-${order.orderId}__interactive`}
              />
            ) : null}
            <Tab
              label={t('dispatch.job.tickets')}
              data-ui={`page-dispatch__order-dispatch-card-expanded-block__order-${order.orderId}__interactive`}
            />
            <Tab
              label={t('order.form.order_details')}
              data-ui={`page-dispatch__order-dispatch-card-expanded-block__order-${order.orderId}__interactive`}
            />
            <Tab
              label={t('live_map.live_map')}
              data-ui={`page-dispatch__order-dispatch-card-expanded-block__order-${order.orderId}__interactive`}
            />
            <Tab
              label={t('truck_replay.truck_replay')}
              data-ui={`page-dispatch__order-dispatch-card-expanded-block__order-${order.orderId}__interactive`}
            />

            {canShowTimeline ? (
              <Tab
                label={t('dispatch.timeline.timeline')}
                data-ui={`page-dispatch__order-dispatch-card-expanded-block__order-${order.orderId}__interactive`}
              />
            ) : null}

            {order.messagingEnabled ? (
              <Tab
                sx={
                  hasUnreadMessages
                    ? unreadTabAccentSx(theme.brandV2.colors.treadRed)
                    : undefined
                }
                label={
                  <TabLabelWithAdornment
                    endAdornment={<UnreadCountBadge count={orderUnreadCount} />}
                    label={t('messaging.order_tab.title')}
                  />
                }
                data-ui={`page-dispatch__order-dispatch-card-expanded-block__order-${order.orderId}__interactive`}
              />
            ) : null}
          </Tabs>

          <Box
            sx={
              presentation === 'grid'
                ? { alignItems: 'center', display: 'flex', gap: 1 }
                : undefined
            }
          >
            {!editMode &&
              (canEditJobs || canAddNewJobs) &&
              selectedTab === OrderDispatchCardExpandedBlockTab.JOBS && (
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    gap: 1,
                    height: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
                  }}
                >
                  {canEditJob && (
                    <SmallButton
                      color="brandV2Orange"
                      variant="text"
                      onClick={(event) => {
                        event.stopPropagation();
                        setEditMode(EditMode.ORDER_TRUCKS_COUNT);
                      }}
                      sx={{
                        '&.MuiButtonBase-root': {
                          border: 'solid 1px transparent',
                          '&:hover': { borderColor: theme.brandV2.colors.treadOrange },
                        },
                      }}
                    >
                      {t('dispatch.order.edit_trucks_count')}
                    </SmallButton>
                  )}

                  {canAddNewJobs && (
                    <SmallButton
                      color="brandV2Orange"
                      data-ui={`page-dispatch__order-${order.orderId}__add-job-button`}
                      variant="text"
                      startIcon={<AddCircleOutline />}
                      onClick={(event) => {
                        event.stopPropagation();
                        onCreateJobFromOrder();
                      }}
                      sx={{
                        '&.MuiButtonBase-root': {
                          border: 'solid 1px transparent',
                          px: 1,
                          '.MuiButton-startIcon': { mr: 0.5 },
                          '.MuiSvgIcon-root': { fontSize: '16px' },
                          '&:hover': { borderColor: theme.brandV2.colors.treadOrange },
                        },
                      }}
                    >
                      {t('form_fields.job')}
                    </SmallButton>
                  )}
                </Box>
              )}

            {editMode === EditMode.ORDER_TRUCKS_COUNT && (
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  gap: 1,
                  height: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
                }}
              >
                <OrderTrucksCountForm
                  ref={orderTrucksCountFormRef}
                  isSubmitting={isUpdatingAfterEditMode}
                  order={order}
                />

                <Divider flexItem orientation="vertical" />

                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    gap: 1,
                    height: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
                  }}
                >
                  <SmallButton
                    color="secondary"
                    disabled={isUpdatingAfterEditMode}
                    onClick={handleCancelEditModeClick}
                    variant="outlined"
                  >
                    {t('actions.cancel')}
                  </SmallButton>

                  <SmallLoadingButton
                    color="brandV2Yellow"
                    loading={isUpdatingAfterEditMode}
                    onClick={handleFormSubmit}
                  >
                    {t('actions.save')}
                  </SmallLoadingButton>
                </Box>
              </Box>
            )}

            {jobsTablePresentation === 'grid' &&
              selectedTab === OrderDispatchCardExpandedBlockTab.JOBS && (
                <>
                  <Box
                    ref={setJobsTableActionsContainer}
                    sx={{ alignItems: 'center', display: 'flex', gap: 1 }}
                  />
                  <Box ref={setJobsTableColumnsMenuContainer} sx={{ display: 'flex' }} />
                </>
              )}
          </Box>
        </Box>

        <MessagingProviders>
          <Box>
            {selectedTab === OrderDispatchCardExpandedBlockTab.JOBS && (
              <FormProvider {...editJobTableRowsForm}>
                <JobsTable
                  actionsContainer={jobsTableActionsContainer}
                  checkedJobs={checkedJobs}
                  columnsMenuContainer={jobsTableColumnsMenuContainer}
                  isEditingJobsTableRows={editMode === EditMode.JOBS_TABLE_ROWS}
                  isEditingOrderTrucksCount={editMode === EditMode.ORDER_TRUCKS_COUNT}
                  onCheckedStateChange={onCheckedJobStateChange}
                  onEditJobsTableRowsModeChange={(isEditingJobsTableRows) => {
                    if (isEditingJobsTableRows) {
                      setEditMode(EditMode.JOBS_TABLE_ROWS);
                    } else {
                      setEditMode(undefined);
                    }
                  }}
                  onSubmitEditJobsTableRowsForm={handleFormSubmit}
                  order={order}
                  presentation={jobsTablePresentation}
                  readOnly={readOnly}
                />
              </FormProvider>
            )}

            {selectedTab === OrderDispatchCardExpandedBlockTab.TICKETS && (
              <Box
                sx={{
                  p: 1,
                }}
              >
                <OrderTickets order={order} />
              </Box>
            )}

            {selectedTab === OrderDispatchCardExpandedBlockTab.ORDER_DETAILS && (
              <Box
                sx={{
                  p: 1,
                }}
              >
                <OrderDetails order={order} />
              </Box>
            )}

            {selectedTab === OrderDispatchCardExpandedBlockTab.LIVE_MAP && (
              <Box
                sx={{
                  m: 1,
                  borderRadius: 1,
                  overflow: 'hidden',
                  border: 1,
                  background: theme.semantic.surfaceRaised,
                  height: 400,
                  borderColor: theme.brandV2.colors.treadGray7,
                }}
              >
                <TreadLiveMapV2 order={order} />
              </Box>
            )}

            {selectedTab === OrderDispatchCardExpandedBlockTab.TRUCK_REPLAY && (
              <Box
                sx={{
                  m: 1,
                  borderRadius: 1,
                  overflow: 'hidden',
                  border: 1,
                  background: theme.semantic.surfaceRaised,
                  borderColor: theme.brandV2.colors.treadGray7,
                }}
              >
                <TruckReplayTab order={order} />
              </Box>
            )}

            {selectedTab === OrderDispatchCardExpandedBlockTab.TIMELINE && (
              <OrderAuditLog order={order} />
            )}

            {selectedTab === OrderDispatchCardExpandedBlockTab.LOADS && (
              <LoadsTabContent filters={loadsFilters} order={order} />
            )}

            {selectedTab === OrderDispatchCardExpandedBlockTab.METRICS && (
              <OrderMetricsTab order={order} />
            )}

            {selectedTab === OrderDispatchCardExpandedBlockTab.MESSAGING && (
              <Box sx={{ p: 2 }}>
                {/* Remounting lands every Message all drivers request on the Order thread. */}
                <OrderMessagingTab
                  key={messagingTabRequestCount}
                  order={order}
                  readOnly={readOnly}
                />
              </Box>
            )}
          </Box>
        </MessagingProviders>
      </Box>
    );
  },
);

export default OrderDispatchCardExpandedBlock;
